import { useMemo, useCallback } from "react";
import type { FileItem, ViewMode } from "../../types";
import { createFileGalleryColumns } from "./FileGalleryColumns";
import { FileGalleryTable } from "./FileGalleryTable";
import { FileGalleryEmptyState } from "./FileGalleryEmptyState";
import { GalleryGrid } from "./GalleryGrid";
import { SortIcon } from "../shared/SortIcon";
import { useTableSorting } from "../../hooks/gallery/useTableSorting";
import { extractAttributeColumns } from "../../utils/fileUtils";
import type { DisplayAttribute } from "@/features/admin/panels/categories/hooks/useCategoryAttributesDisplay";

interface FileGalleryProps {
  files: FileItem[];
  viewMode: ViewMode;
  onFileSelect: (file: FileItem) => void;
  selectedFile: FileItem | null;
  selectedFileIds?: Set<string>;
  onMultiSelect?: (file: FileItem, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
  onSort?: (column: string) => void;
  sortBy?: string;
  sortDirection?: "ASC" | "DESC";
  onImageClick?: (file: FileItem) => void;
  categoryAttributes?: DisplayAttribute[];
  attributeOrder?: string[];
}

export function FileGallery({
  files,
  viewMode,
  onFileSelect,
  selectedFile,
  selectedFileIds,
  onMultiSelect,
  onSelectAll,
  onSort,
  sortBy,
  sortDirection,
  onImageClick,
  categoryAttributes,
  attributeOrder,
}: FileGalleryProps) {
  const { handleSortClick } = useTableSorting({
    onSort,
    sortBy,
    sortDirection,
  });

  const attributeColumns = useMemo(() => {
    if (attributeOrder && attributeOrder.length > 0) {
      return attributeOrder;
    }
    if (categoryAttributes && categoryAttributes.length > 0) {
      return categoryAttributes
        .sort((a, b) => a.order - b.order)
        .map((attr) => attr.name);
    }
    return extractAttributeColumns(files);
  }, [files, categoryAttributes, attributeOrder]);

  const getSortIcon = useCallback(
    (column: string) => (
      <SortIcon column={column} sortBy={sortBy} sortDirection={sortDirection} />
    ),
    [sortBy, sortDirection]
  );

  const columns = useMemo(
    () =>
      createFileGalleryColumns(
        attributeColumns,
        {
          onSort,
          sortBy,
          sortDirection,
          getSortIcon,
          handleSortClick,
          selectedFileIds,
          onMultiSelect,
          onSelectAll,
          totalFiles: files.length,
        },
        categoryAttributes
      ),
    [
      attributeColumns,
      onSort,
      sortBy,
      sortDirection,
      getSortIcon,
      handleSortClick,
      selectedFileIds,
      onMultiSelect,
      onSelectAll,
      files.length,
      categoryAttributes,
    ]
  );

  if (files.length === 0) {
    return <FileGalleryEmptyState />;
  }

  if (viewMode === "table") {
    return (
      <FileGalleryTable
        files={files}
        columns={columns}
        selectedFile={selectedFile}
        selectedFileIds={selectedFileIds}
        onFileSelect={onFileSelect}
      />
    );
  }

  if (viewMode === "large-gallery") {
    return (
      <GalleryGrid
        files={files}
        selectedFile={selectedFile}
        selectedFileIds={selectedFileIds}
        onFileSelect={onFileSelect}
        onMultiSelect={onMultiSelect}
        onImageClick={onImageClick}
        isLargeGallery={true}
      />
    );
  }

  return (
    <GalleryGrid
      files={files}
      selectedFile={selectedFile}
      selectedFileIds={selectedFileIds}
      onFileSelect={onFileSelect}
      onMultiSelect={onMultiSelect}
      onImageClick={onImageClick}
      isLargeGallery={false}
    />
  );
}
